import {useEffect, useRef, useState} from 'react';
import {useSelector} from "react-redux";
import {
  selectGameResults,
  selectIsGameStarted,
  selectPhase,
  selectRound,
  selectWarPhaseStage
} from "../../redux/reducers/sharedStateReducer/selectors";
import {selectIsTutorialActive} from "../../redux/reducers/privateStateReducer/selectors";
import {useIsCurrentUserTurn} from "../../utils/gameState/user/useIsCurrentUserCurrentPlayer";
import {PHASES, WAR_PHASE_STAGES} from "../../constants/phases";
import {
  GAME_FIELD_READY_EVENT,
  INITIAL_MUSIC_VOLUME,
  LOCAL_STORAGE_KEY_MUSIC_VOLUME
} from "../../constants/sound";
import {BACKGROUND_MUSIC_TRACKS, MATCH_MUSIC_SILENT_ON_OWN_TURN} from "../../configs/backgroundMusic";
import {createBackgroundMusicPlayer, createLoopMusicProgram} from "../../utils/sound/createBackgroundMusicPlayer";
import {getMatchMusicBlock, getMatchMusicProgram} from "../../utils/sound/matchMusicProgram";

function getMusicVolume() {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY_MUSIC_VOLUME);
    if (stored === null) return INITIAL_MUSIC_VOLUME;
    const parsed = parseFloat(stored);
    if (isNaN(parsed)) return INITIAL_MUSIC_VOLUME;
    return Math.max(0, Math.min(1, parsed));
  } catch (error) {
    return INITIAL_MUSIC_VOLUME;
  }
}

/**
 * Играет ли музыка ПАРТИИ (на экране партии). До ее старта и после конца —
 * играет; в расстановке стартовых кварталов — тишина у всех, и у зрителя
 * тоже; в остальное время — играет.
 *
 * Правило «на своем ходу музыка молчит» (isSilentOnOwnTurn, по умолчанию —
 * MATCH_MUSIC_SILENT_ON_OWN_TURN) сейчас выключено. Включенное, оно глушит
 * музыку, пока ход у пользователя; одновременная фаза снабжения сетевой игры
 * (isParallelSupply) для него — свой ход у каждого (useIsCurrentUserTurn):
 * музыка молчит, пока игрок раздает снабжение, и возвращается, когда он
 * закончил свою часть фазы и ждет остальных. Ближний бой, хотя в нем
 * действуют все разом, идет с музыкой при любой настройке — у игроков и у
 * зрителя (раньше молчал и он: зритель слышал, как музыка гаснет на каждом
 * бою).
 */
export const checkIfBackgroundMusicPlaying = ({
  isGameStarted,
  gameResults,
  isCurrentUserTurn,
  phase,
  warPhaseStage,
  isSilentOnOwnTurn = MATCH_MUSIC_SILENT_ON_OWN_TURN,
}) => {
  if (!isGameStarted || gameResults) {
    return true;
  }
  if (phase === PHASES.SETUP) {
    return false;
  }
  if (phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.COMBAT) {
    return true;
  }
  return !(isSilentOnOwnTurn && isCurrentUserTurn);
};

// Жесты пользователя, которыми браузер разрешает запускать звук. Не только
// клик: касание игрового поля клика не дает (Phaser гасит его на канве), и
// игрок, который ходит одними касаниями поля, музыку не разблокировал бы
export const MUSIC_GESTURE_EVENTS = ['click', 'pointerup', 'touchend', 'keydown'];

// Музыка меню: один трек по кругу
export const MENU_MUSIC_PROGRAM = createLoopMusicProgram(BACKGROUND_MUSIC_TRACKS.MENU);

/**
 * Что должно звучать фоном: программа музыки и играет ли она сейчас.
 *
 * Вне партии — трек меню, и играет он всегда. Состояние партии в сторе там не
 * в счет: на главном меню крутится демо-партия (запись чужого матча), и ее
 * расстановка или ближний бой не должны глушить музыку меню.
 *
 * На экране партии — музыка партии (треки блока раундов вперемежку с темой
 * партии, см. MATCH_MUSIC_ROUND_BLOCKS), по правилу checkIfBackgroundMusicPlaying.
 * Но только когда поле загружено: до тех пор — тишина без программы (трек
 * меню гаснет, трек партии не загружается), чтобы музыка не отнимала канал у
 * картинок и звуков поля на медленном интернете.
 *
 * @param {Object} params
 * @param {boolean} params.isInGame - открыт экран партии (SCREENS.GAME)
 * @param {*} params.isGameMusicPlaying - решение checkIfBackgroundMusicPlaying
 *   (важна его истинность)
 * @param {boolean} [params.isGameFieldReady] - поле партии загружено
 *   (GAME_FIELD_READY_EVENT)
 * @param {number} [params.round] - текущий раунд партии
 * @param {boolean} [params.isTutorial] - идет глава обучения
 * @returns {{program: Object|null, isPlaying: boolean}}
 */
export const resolveBackgroundMusic = ({
  isInGame,
  isGameMusicPlaying,
  isGameFieldReady = false,
  round = 0,
  isTutorial = false,
}) => {
  if (!isInGame) {
    return {program: MENU_MUSIC_PROGRAM, isPlaying: true};
  }
  if (!isGameFieldReady) {
    return {program: null, isPlaying: false};
  }
  return {
    program: getMatchMusicProgram(getMatchMusicBlock({round, isTutorial})),
    isPlaying: Boolean(isGameMusicPlaying),
  };
};

/**
 * Загружено ли поле партии с тех пор, как открыт экран партии. Поле шлет
 * GAME_FIELD_READY_EVENT (GameContent); вышли из партии — признак сброшен, и
 * следующая партия снова ждет свое поле
 * @param {boolean} isInGame
 */
export const useIsGameFieldReady = (isInGame) => {
  const [isGameFieldReady, setIsGameFieldReady] = useState(false);

  useEffect(() => {
    if (!isInGame) {
      setIsGameFieldReady(false);
      return undefined;
    }
    const handleFieldReady = () => setIsGameFieldReady(true);
    window.addEventListener(GAME_FIELD_READY_EVENT, handleFieldReady);
    return () => window.removeEventListener(GAME_FIELD_READY_EVENT, handleFieldReady);
  }, [isInGame]);

  return isInGame && isGameFieldReady;
};

/**
 * Фоновая музыка приложения: в меню — свой трек, в партии — своя очередь
 * треков.
 * @param {Object} props
 * @param {boolean} [props.isInGame] - открыт экран партии (SCREENS.GAME)
 */
function BackgroundMusic({isInGame = false}) {
  const isGameStarted = useSelector(selectIsGameStarted);
  const gameResults = useSelector(selectGameResults);
  const isCurrentUserTurn = useIsCurrentUserTurn();
  const phase = useSelector(selectPhase);
  const warPhaseStage = useSelector(selectWarPhaseStage);
  const round = useSelector(selectRound);
  const isTutorial = useSelector(selectIsTutorialActive);
  const isGameFieldReady = useIsGameFieldReady(isInGame);

  const isGameMusicPlaying = checkIfBackgroundMusicPlaying({
    isGameStarted,
    gameResults,
    isCurrentUserTurn,
    phase,
    warPhaseStage,
  });
  const {program, isPlaying} = resolveBackgroundMusic({
    isInGame,
    isGameMusicPlaying,
    isGameFieldReady,
    round,
    isTutorial,
  });

  const playerRef = useRef(null);

  useEffect(() => {
    const player = createBackgroundMusicPlayer({getVolume: getMusicVolume});
    playerRef.current = player;

    // Автовоспроизведение до первого взаимодействия со страницей браузер
    // блокирует — запуск повторяется по жесту. После первого жеста слушатели
    // не снимаются: следующим же музыка вернется, если ее прервала система
    // (звонок на телефоне). На фазе перехвата — чтобы жест дошел, даже если
    // интерфейс остановил всплытие события
    const handleUserGesture = () => player.handleUserGesture();
    const handleVolumeChange = () => player.refreshVolume();
    MUSIC_GESTURE_EVENTS.forEach((eventName) => document.addEventListener(eventName, handleUserGesture, true));
    window.addEventListener('musicvolumechange', handleVolumeChange);

    return () => {
      MUSIC_GESTURE_EVENTS.forEach((eventName) => document.removeEventListener(eventName, handleUserGesture, true));
      window.removeEventListener('musicvolumechange', handleVolumeChange);
      player.destroy();
      playerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (playerRef.current) {
      playerRef.current.setDesired({program, isPlaying});
    }
  }, [program, isPlaying]);

  return null;
}

export default BackgroundMusic;
